import { MenuService } from '@/layout/service/menu.service';
import { Pessoa } from '@/shared/models/pessoa-info.model';
import { PessoaResponseModel } from '@/shared/components/pessoa/pesquisa/pessoa.model';
import { inject, Component, ChangeDetectionStrategy, OnInit, signal } from '@angular/core';
import { LoggerService } from '@/core/services/logger.service';
import { Router, RouterModule } from '@angular/router';
import { Pesquisa } from '@/shared/components/pessoa/pesquisa/pesquisa';
import { createMedicosMenu } from './medicos.constants';
import { PessoaService } from '@/shared/services/pessoa.service';

/**
 * Componente principal (layout) para o módulo de Médicos.
 * Responsável por renderizar as rotas filhas, gerenciar o menu lateral
 * e controlar a exibição do diálogo de pesquisa de pessoas.
 */
@Component({
    selector: 'app-medicos-content',
    standalone: true,
    imports: [RouterModule, Pesquisa],
    template: `
        <!-- No seu componente de layout principal -->
        <main class="w-full">
            <div>
                <router-outlet></router-outlet>
            </div>
        </main>
        <app-pesquisa [showDialog]="pesquisarDialog()" (showDialogChange)="pesquisarDialog.set($event)" (pessoa)="carregarPessoa($event)"></app-pesquisa>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MedicosContent implements OnInit {
    private readonly logger = inject(LoggerService);
    /** Controla a visibilidade do diálogo de pesquisa (signal: o menu global é OnPush e precisa reagir sozinho). */
    readonly pesquisarDialog = signal(false);

    /** Caminho base para as rotas do módulo. */
    rootPath = 'medicos';

    /** Estrutura do menu de navegação específico para o módulo de Médicos. */
    menu = createMedicosMenu({ pesquisarPessoa: () => this.pesquisarDialog.set(true) });

    /**
     * Construtor para injetar serviços essenciais.
     * @param menuService - Serviço para gerenciar o estado do menu principal da aplicação.
     * @param router - Serviço do Angular para controle de navegação.
     */
    constructor(
        private menuService: MenuService,
        private router: Router,
        private pessoaService: PessoaService
    ) {}

    /**
     * Hook do ciclo de vida. Executado na inicialização do componente.
     * Define o menu específico deste módulo no serviço de menu global.
     */
    ngOnInit(): void {
        this.limparPessoaDeOutroModulo();
        this.menuService.setMenu(this.menu);
        this.menuService.setSistema('SICOM | Médicos');
    }

    /**
     * Descarta a pessoa em sessão se ela pertencer a outro módulo (ex.: um prestador
     * selecionado em Prestadores) — evita que o card de relatórios de médico venha
     * pré-preenchido com a seleção de um módulo diferente.
     */
    private limparPessoaDeOutroModulo(): void {
        const referencia = this.pessoaService.getPessoaReference();
        if (referencia?.tipo && referencia.tipo !== 'MEDICO') {
            this.pessoaService.clearPessoa();
        }
    }

    /**
     * Força o recarregamento do componente da rota especificada, mesmo que já esteja ativa.
     * Navega para uma rota temporária e depois retorna à original para forçar a reinicialização.
     * @param rota A rota de destino para recarregar.
     */
    private async executar(rota: string) {
        await this.router.navigateByUrl('/', { skipLocationChange: true });
        await this.router.navigate([rota]);
    }

    /**
     * Callback executado quando uma pessoa é selecionada no diálogo de pesquisa.
     * Salva os dados da pessoa na sessão e recarrega a rota principal do módulo.
     * @param pessoa O objeto da pessoa selecionada.
     */
    carregarPessoa(pessoa?: Pessoa | PessoaResponseModel) {
        if (pessoa?.id && pessoa.uf) {
            this.pessoaService.setPessoa({ ...pessoa, id: pessoa.id, uf: pessoa.uf });
            this.executar(this.rootPath);
        } else {
            this.logger.error('Registro não encontrado!');
        }
    }
}
